// AI Functions API Service

// API functions for the bldt-ai-fn endpoints (document AI, condition matching).
// Uses REACT_APP_AI_FN_URL for the base URL.

import type {
	AiBlobResponse,
	AiDocumentQuery,
	AiDocumentResponse,
	AiDocumentResultsBulkResponse,
	AiDocumentResultsResponse,
	AiDocumentUpdatePayload,
	AiDocumentUpdateResponse,
	AiUploadPayload,
	AiUploadResponse,
} from '../components/pages/ProjectV2/Documents/documentTypes';

import {
	CONDITION_GENERATION_PROMPT,
	parseAndValidateResponse,
	type AiConditionResult,
	type GenerateConditionsResponse,
} from './conditionGenerationPrompt';

// Config

const AI_FN_URL = process.env.REACT_APP_AI_FN_URL || 'http://localhost:7072/api';

/**
 * Environment scope used to segregate AI document uploads across local/dev/qa/prod.
 * The same backend storage may be shared between a deployed environment and local
 * developer machines (since the Document Insight pipeline only exists in dev+).
 * Tagging every upload + every list query with this value keeps each environment's
 * users from seeing each other's documents.
 */
const AI_ENVIRONMENT = process.env.REACT_APP_ENV || 'unknown';

/** Get Authorization + User headers matching bldt-ui-fn convention. */
const getAiAuthHeaders = (): Record<string, string> => {
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');
	const headers: Record<string, string> = {};
	if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
	if (idToken) headers.User = idToken;
	return headers;
};

// Helpers

const buildAiQueryString = (params: Record<string, string | number | undefined>): string => {
	const searchParams = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value !== undefined && value !== '' && value !== 'all') {
			searchParams.append(key, String(value));
		}
	});
	const qs = searchParams.toString();
	return qs ? `?${qs}` : '';
};

// Documents

/**
 * Fetch documents from the AI function get_documents endpoint.
 * Supports search, filtering, sorting, and pagination — all server-side.
 *
 * @param query - Query parameters for filtering/sorting/pagination
 * @returns AiDocumentResponse with data, pagination, and stats
 */
export const fetchAiDocuments = async (
	query: AiDocumentQuery = {}
): Promise<AiDocumentResponse> => {
	const effective: AiDocumentQuery = { environment: AI_ENVIRONMENT, ...query };
	const qs = buildAiQueryString(effective as Record<string, string | number | undefined>);
	const res = await fetch(`${AI_FN_URL}/get_documents${qs}`, {
		method: 'GET',
		headers: { 'Content-Type': 'application/json', ...getAiAuthHeaders() },
	});

	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new Error(body.error || `AI API error: ${res.status}`);
	}

	return res.json();
};

// Document Results (processing blobs)

/**
 * Fetch processing result blobs for a single document by its job_id.
 * Each stage (silver, pre-gold, gold) produces result rows with blob references.
 *
 * @param jobId - The job UUID from the upload response or document record
 * @returns AiDocumentResultsResponse with array of blob results
 */
export const fetchAiDocumentResults = async (jobId: string): Promise<AiDocumentResultsResponse> => {
	const qs = buildAiQueryString({
		job_id: jobId,
	});
	const res = await fetch(`${AI_FN_URL}/get_document_results${qs}`, {
		method: 'GET',
		headers: { 'Content-Type': 'application/json', ...getAiAuthHeaders() },
	});

	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new Error(body.error || `AI API error: ${res.status}`);
	}

	return res.json();
};

/**
 * Fetch processing result blobs for multiple documents in a single request.
 * Returns a map of job_id → blobs, useful for determining which documents
 * on the All Documents page have AI insights available.
 *
 * @param jobIds - Array of job UUIDs to query
 * @returns AiDocumentResultsBulkResponse keyed by job_id
 */
export const fetchAiDocumentResultsBulk = async (
	jobIds: string[]
): Promise<AiDocumentResultsBulkResponse> => {
	if (jobIds.length === 0) return { results: {} };

	const qs = buildAiQueryString({
		job_id: jobIds.join(','),
	});
	const res = await fetch(`${AI_FN_URL}/get_document_results${qs}`, {
		method: 'GET',
		headers: { 'Content-Type': 'application/json', ...getAiAuthHeaders() },
	});

	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new Error(body.error || `AI API error: ${res.status}`);
	}

	// Single job_id still returns flat { job_id, blobs }, wrap it
	if (jobIds.length === 1) {
		const single: AiDocumentResultsResponse = await res.json();
		return { results: { [single.job_id]: single } };
	}

	return res.json();
};

// Read Blob

/**
 * Read blob content from Azure Storage via the AI function read_blob endpoint.
 * Returns the raw content (text or base64 for binary) along with content_type.
 *
 * @param container - Storage container name (e.g. "bronze")
 * @param blob - Blob path/name (e.g. "document.pdf")
 * @returns AiBlobResponse with content or content_base64
 */
export const fetchAiBlob = async (container: string, blob: string): Promise<AiBlobResponse> => {
	const qs = buildAiQueryString({ container, blob });
	const res = await fetch(`${AI_FN_URL}/read_blob${qs}`, {
		method: 'GET',
		headers: { ...getAiAuthHeaders() },
	});

	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new Error(body.error || `AI API error: ${res.status}`);
	}

	return res.json();
};

/**
 * Get a blob URL that renders content directly in an iframe / object tag.
 * Fetches the blob via read_blob and creates an object URL from the content.
 *
 * @param container - Storage container name
 * @param blob - Blob name
 * @returns Object URL string for embedding, plus content_type
 */
/** Infer MIME type from filename extension when the API returns a generic type */
const inferContentType = (filename: string, apiContentType: string): string => {
	if (apiContentType && apiContentType !== 'application/octet-stream') return apiContentType;
	const ext = filename.split('.').pop()?.toLowerCase();
	const map: Record<string, string> = {
		pdf: 'application/pdf',
		doc: 'application/msword',
		docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
		xls: 'application/vnd.ms-excel',
		xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		csv: 'text/csv',
		jpg: 'image/jpeg',
		jpeg: 'image/jpeg',
		png: 'image/png',
		gif: 'image/gif',
		txt: 'text/plain',
		json: 'application/json',
		html: 'text/html',
		xml: 'application/xml',
	};
	return (ext && map[ext]) || apiContentType || 'application/octet-stream';
};

export const fetchAiBlobObjectUrl = async (
	container: string,
	blob: string
): Promise<{ objectUrl: string; contentType: string }> => {
	const data = await fetchAiBlob(container, blob);
	const contentType = inferContentType(blob, data.content_type);

	let blobData: Blob;
	if (data.content_base64) {
		// Binary content — decode base64
		const byteString = atob(data.content_base64);
		const bytes = new Uint8Array(byteString.length);
		for (let i = 0; i < byteString.length; i++) {
			bytes[i] = byteString.charCodeAt(i);
		}
		blobData = new Blob([bytes], { type: contentType });
	} else if (typeof data.content === 'string') {
		// Text content
		blobData = new Blob([data.content], { type: contentType });
	} else {
		// JSON content
		blobData = new Blob([JSON.stringify(data.content, null, 2)], { type: 'application/json' });
	}

	return { objectUrl: URL.createObjectURL(blobData), contentType };
};

// Upload Document

/**
 * Upload a document to Azure Blob Storage and create a DB record.
 * Uses multipart/form-data to send the file and metadata.
 *
 * @param payload - File and metadata fields
 * @returns AiUploadResponse with document_id, blob_uri, etc.
 */
export const uploadAiDocument = async (payload: AiUploadPayload): Promise<AiUploadResponse> => {
	const formData = new FormData();
	formData.append('file', payload.file);

	if (payload.doc_type) formData.append('doc_type', payload.doc_type);
	if (payload.contract_id) formData.append('contract_id', payload.contract_id);
	if (payload.tag) formData.append('tag', payload.tag);
	if (payload.project_id) formData.append('project_id', String(payload.project_id));
	if (payload.source_type) formData.append('source_type', payload.source_type);
	if (payload.source_id) formData.append('source_id', String(payload.source_id));
	if (payload.date) formData.append('date', payload.date);
	formData.append('environment', payload.environment || AI_ENVIRONMENT);

	const res = await fetch(`${AI_FN_URL}/upload_document`, {
		method: 'POST',
		headers: { ...getAiAuthHeaders() },
		body: formData,
		// No Content-Type header — browser sets multipart boundary automatically
	});

	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new Error(body.error || `Upload failed: ${res.status}`);
	}

	return res.json();
};

// Update Document

/**
 * Partially update a document record (tags, filename, doc_type, etc.).
 *
 * @param payload - Fields to update. `id` is required; all others are optional.
 * @returns AiDocumentUpdateResponse with the full updated document
 */
export const updateAiDocument = async (
	payload: AiDocumentUpdatePayload
): Promise<AiDocumentUpdateResponse> => {
	const res = await fetch(`${AI_FN_URL}/update_document`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json', ...getAiAuthHeaders() },
		body: JSON.stringify(payload),
	});

	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new Error(body.error || `Update failed: ${res.status}`);
	}

	return res.json();
};

// Direct Prompt (Chat / LLM)

export interface AiPromptRequest {
	prompt: string;
	max_tokens?: number;
	temperature?: number;
}

export interface AiPromptResponse {
	prompt: string;
	response: string;
	model: string;
	service: string;
	endpoint: string;
	duration_ms: number;
}

/**
 * Send a prompt to the direct_prompt LLM endpoint.
 * Supports both GET (simple query) and POST (with options).
 *
 * @param request - Prompt text and optional parameters
 * @returns AiPromptResponse with LLM response, model info, and timing
 */
export const sendAiPrompt = async (request: AiPromptRequest): Promise<AiPromptResponse> => {
	const res = await fetch(`${AI_FN_URL}/prompt`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', ...getAiAuthHeaders() },
		body: JSON.stringify(request),
	});

	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new Error(body.error || `Prompt failed: ${res.status}`);
	}

	return res.json();
};

// Chat Endpoint — Stateless chat via POST /api/chat
// The server keeps no memory; the client sends full message history each time.

export interface LlmMessage {
	role: 'system' | 'user' | 'assistant';
	content: string;
}

export interface ChatRequest {
	/** Full conversation history */
	messages: LlmMessage[];
	/** Optional system instruction prepended to the conversation */
	system_prompt?: string;
}

export interface ChatResponse {
	reply: string;
	model: string;
	service: string;
	duration_ms: number;
}

/**
 * Send a stateless chat request to POST /api/chat.
 * The client is responsible for accumulating conversation history.
 *
 * @param request - Messages array and optional system_prompt
 * @returns ChatResponse with the assistant reply, model, service, and latency
 */
export const sendChat = async (request: ChatRequest): Promise<ChatResponse> => {
	const res = await fetch(`${AI_FN_URL}/chat`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', ...getAiAuthHeaders() },
		body: JSON.stringify(request),
	});

	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new Error(body.error || `Chat request failed: ${res.status}`);
	}

	return res.json();
};

// RAG Chat Endpoint — Agentic RAG via POST /api/rag_chat
// AI agent with document search/list tools, scoped to a project.
// Stateless — full message history must be sent each time.

export interface RagChatRequest {
	/** Project ID — scopes all document access to this project */
	project_id: number;
	/** Full conversation history */
	messages: LlmMessage[];
	/** Optional additional system instructions appended to the built-in BLDT prompt */
	system_prompt?: string;
}

export interface RagToolCall {
	tool: string;
	query?: string;
	params?: Record<string, string>;
	results_count?: number;
	total_count?: number;
	skipped_out_of_project?: number;
	filenames_count?: number;
	duration_ms?: number;
	top?: number;
	project_id?: number;
}

export interface RagChatResponse {
	reply: string;
	tool_calls?: RagToolCall[];
	intermediate_messages?: Array<{
		type: string;
		function?: string;
		arguments?: Record<string, unknown>;
		result_preview?: string;
	}>;
	model: string;
	service: string;
	duration_ms: number;
}

/**
 * Send a request to the agentic RAG chat endpoint.
 * The agent can autonomously search and list project documents.
 *
 * @param request - Project ID, messages array, and optional system_prompt
 * @param signal  - Optional AbortSignal to cancel the request
 * @returns RagChatResponse with the agent reply, tool call trace, and metadata
 */
export const sendRagChat = async (
	request: RagChatRequest,
	signal?: AbortSignal
): Promise<RagChatResponse> => {
	const res = await fetch(`${AI_FN_URL}/rag_chat`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', ...getAiAuthHeaders() },
		body: JSON.stringify(request),
		signal,
	});

	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new Error(body.error || `RAG chat request failed: ${res.status}`);
	}

	return res.json();
};

// Condition Generation — AI-powered milestone extraction via POST /api/chat

export type { AiConditionResult, GenerateConditionsResponse };

export interface ConditionGenerationContext {
	contracts: Array<{ id: number; name: string; amount: number; currency: string }>;
	existingConditions: Array<{ name: string; paymentPercentage: number | null }>;
	documentInsights?: Array<{
		persona: string;
		summary: string;
		keyInformationTable?: Record<string, string>;
		checklist?: string[];
		disbursementConditions?: string;
		timelines?: string;
		responsibleParties?: string;
		keyRisks?: string;
	}>;
}

/**
 * Generate payment milestone conditions from a user prompt and project context.
 * Uses the structured output system prompt to get JSON-formatted milestones
 * from the LLM, then parses and validates the response.
 */
export const generateConditions = async (
	userPrompt: string,
	context: ConditionGenerationContext
): Promise<GenerateConditionsResponse> => {
	const response = await sendChat({
		system_prompt: CONDITION_GENERATION_PROMPT,
		messages: [
			{
				role: 'user',
				content: JSON.stringify({
					userRequest: userPrompt,
					...context,
				}),
			},
		],
	});

	return parseAndValidateResponse(response.reply);
};
